/** Shared watermark — UI badge + screenshot export mark. */
export const WATERMARK_BADGE_TEXT = 'investmentbastion.com';
/** Brand name for the signature mark (the domain sits under it, muted). */
export const WATERMARK_BRAND_NAME = 'Investment Bastion';

/**
 * Font for the HTML signature inside html-to-image exports (share windows).
 * Those captures run with `skipFonts`, so next/font families are missing from
 * the PNG and text falls back to the system face — wider than Inter Tight —
 * while html-to-image keeps the widths measured on the page. A right-aligned
 * mark then spills past the content edge and gets cropped. Rendering the mark
 * in the system face on the page too keeps preview and PNG metrics identical.
 */
export const WATERMARK_EXPORT_SAFE_FONT =
  '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

export const WATERMARK_BADGE_STYLE = {
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '0.02em',
  iconSize: 20,
  padX: 14,
  padY: 6,
  gap: 8,
  borderRadius: 8,
  background: 'rgba(59, 213, 118, 0.12)',
  border: '1px solid rgba(59, 213, 118, 0.35)',
  color: 'rgba(255, 255, 255, 0.95)',
  fontFamily: 'var(--font-geist-mono), monospace',
} as const;

export function getWatermarkMonoFamily(): string {
  if (typeof document === 'undefined') return 'Geist Mono';
  const v = getComputedStyle(document.documentElement).getPropertyValue('--font-geist-mono').trim();
  return v || 'Geist Mono';
}

/**
 * Canonical Bastion screenshot watermark — the signature used across every
 * terminal panel export (Heatmap, Radar, Stock Charts, share windows).
 *
 * Identity (do not fork per surface): Bastion logo + "Investment Bastion"
 * at the logo's height on one row, the domain on its own row under both,
 * small and muted, both rows flush right. No pill, no glow — it reads as
 * the author's signature, not an ad sticker, and the domain still survives a
 * repost. Only per-surface *placement* (margins) is overridden below.
 *
 * Margins are inset from the panel/content edge (not the outer crop pad).
 */
export const WATERMARK_SCREENSHOT_STYLE = {
  // Same lockup as the landing nav logo (Inter 600, tracking-tight, 21px mark
  // next to 16px text, 8px gap), scaled to 19px text. Mark ≈ 1.3× the type
  // size so it sits level with the name instead of dwarfing it.
  fontSize: 19,
  fontWeight: 600,
  letterSpacing: '-0.025em',
  iconSize: 25,
  padX: 0,
  padY: 0,
  gap: 8,
  dividerGap: 0,
  borderRadius: 0,
  // 0 = top edge of crop content (title). Non-zero drops the mark below the header.
  marginTop: 0,
  marginRight: 14,
  marginBottom: 14,
  placement: 'top-right' as const,
  showPill: false,
  showDivider: false,
  uppercase: false,
  lowercaseTld: false,
  background: 'transparent',
  borderColor: 'transparent',
  divider: 'transparent',
  shadow: 'transparent',
  shadowBlur: 0,
  brandColor: 'rgba(255, 255, 255, 0.9)',
  tldColor: 'rgba(255, 255, 255, 0.45)',
  fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  canvasFontVar: '--font-inter',
  label: WATERMARK_BRAND_NAME,
  subLabel: WATERMARK_BADGE_TEXT,
  subFontSize: 11,
  subFontWeight: 500,
  subColor: 'rgba(255, 255, 255, 0.45)',
  lineGap: 5,
};

/** Heatmap export — right edge flush with the last grid cell. */
export const WATERMARK_SCREENSHOT_STYLE_HEATMAP = {
  ...WATERMARK_SCREENSHOT_STYLE,
  marginRight: 0,
} as const;

/** Stock Charts export — same pill, nudged onto the company-name line. */
export const WATERMARK_SCREENSHOT_STYLE_CHARTS = {
  ...WATERMARK_SCREENSHOT_STYLE,
  marginTop: 2,
  marginRight: 8,
} as const;

/**
 * Compact mark for Factor Brief cards — small, aligned with the card eyebrow.
 */
export const WATERMARK_SCREENSHOT_STYLE_BRIEF = {
  fontSize: 10,
  fontWeight: 600,
  letterSpacing: '0.04em',
  iconSize: 13,
  padX: 0,
  padY: 0,
  gap: 6,
  dividerGap: 0,
  borderRadius: 0,
  marginTop: 12,
  marginRight: 12,
  marginBottom: 12,
  placement: 'top-right' as const,
  showPill: false,
  showDivider: false,
  background: 'transparent',
  borderColor: 'transparent',
  divider: 'transparent',
  shadow: 'transparent',
  shadowBlur: 0,
  brandColor: 'rgba(255, 255, 255, 0.78)',
  tldColor: 'rgba(255, 255, 255, 0.48)',
  fontFamily: 'var(--font-geist-mono), monospace',
} as const;

/** Compact mark for report slides. */
export const WATERMARK_SCREENSHOT_STYLE_COMPACT = {
  ...WATERMARK_SCREENSHOT_STYLE_BRIEF,
  fontSize: 10,
  iconSize: 12,
  gap: 5,
  marginRight: 12,
  marginBottom: 10,
} as const;

/**
 * @deprecated Legacy muted-white no-pill mark. Panel exports now use the
 * canonical green pill (`WATERMARK_SCREENSHOT_STYLE`). Kept only for reference.
 */
export const WATERMARK_SCREENSHOT_STYLE_CLEAN = {
  fontSize: 14,
  fontWeight: 700,
  letterSpacing: '0.06em',
  iconSize: 18,
  padX: 0,
  padY: 0,
  gap: 8,
  dividerGap: 0,
  borderRadius: 0,
  marginTop: 2,
  marginRight: 4,
  marginBottom: 12,
  placement: 'top-right' as const,
  showPill: false,
  showDivider: false,
  uppercase: true,
  background: 'transparent',
  borderColor: 'transparent',
  divider: 'transparent',
  shadow: 'transparent',
  shadowBlur: 0,
  brandColor: 'rgba(255, 255, 255, 0.72)',
  tldColor: 'rgba(255, 255, 255, 0.5)',
  fontFamily: 'var(--font-geist-mono), monospace',
} as const;

export type WatermarkScreenshotStyle = {
  fontSize: number;
  fontWeight: number;
  letterSpacing: string;
  iconSize: number;
  padX: number;
  padY: number;
  gap: number;
  dividerGap: number;
  borderRadius: number;
  marginTop: number;
  marginRight: number;
  marginBottom?: number;
  placement?: 'top-right' | 'bottom-right';
  showPill?: boolean;
  showDivider?: boolean;
  /** Render the site text uppercase (matches the Stock verdict brand row). */
  uppercase?: boolean;
  /** When uppercase, keep the TLD (.com) lowercase for a cleaner wordmark. */
  lowercaseTld?: boolean;
  background: string;
  borderColor: string;
  divider: string;
  shadow: string;
  shadowBlur: number;
  brandColor: string;
  tldColor: string;
  fontFamily: string;
  /**
   * Signature mode: main line (brand name) with an optional muted second line
   * (domain). When `label` is unset the mark falls back to the single-line
   * domain wordmark (Factor Brief / report slides).
   */
  label?: string;
  subLabel?: string | null;
  subFontSize?: number;
  subFontWeight?: number;
  subColor?: string;
  lineGap?: number;
  /** CSS variable holding the next/font family name, for canvas text. */
  canvasFontVar?: string;
};

/** Resolve a next/font CSS variable to a concrete family name for canvas text. */
function resolveCanvasFontFamily(varName: string | undefined, fallback: string): string {
  if (!varName || typeof document === 'undefined') return fallback;
  const v = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
  return v || fallback;
}

/** Signature mark (brand + muted domain) on canvas. Returns true when drawn. */
async function drawSignatureWatermark(
  ctx: CanvasRenderingContext2D,
  s: WatermarkScreenshotStyle,
  px: number,
  badgeRight: number,
  topY: number | null,
  bottomY: number | null,
): Promise<void> {
  const label = s.label ?? WATERMARK_BRAND_NAME;
  const sub = s.subLabel ?? null;
  const fontSize = s.fontSize * px;
  const subFontSize = (s.subFontSize ?? 11) * px;
  const lineGap = (s.lineGap ?? 3) * px;
  const iconSize = s.iconSize * px;
  const gap = s.gap * px;
  const family = resolveCanvasFontFamily(s.canvasFontVar, 'Inter');
  try {
    await Promise.all([
      document.fonts.load(`${s.fontWeight} ${fontSize}px ${family}`),
      document.fonts.load(`${s.subFontWeight ?? 500} ${subFontSize}px ${family}`),
    ]);
  } catch {
    /* fallback stack */
  }
  const setLs = (value: string) => {
    if ('letterSpacing' in ctx) {
      (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = value;
    }
  };

  const mainFont = `${s.fontWeight} ${fontSize}px ${family}, Inter, sans-serif`;
  const subFont = `${s.subFontWeight ?? 500} ${subFontSize}px ${family}, Inter, sans-serif`;
  ctx.font = mainFont;
  setLs(`${parseFloat(s.letterSpacing) * fontSize}px`);
  const labelW = ctx.measureText(label).width;
  // Row 1: logo + name (name at the logo's height). Row 2: domain under both.
  // Both rows are flush right with the content edge.
  const rowW = iconSize + gap + labelW;
  const rowH = Math.max(iconSize, fontSize);
  const blockH = sub ? rowH + lineGap + subFontSize : rowH;
  const bx = badgeRight - rowW;
  const by = topY != null ? topY : (bottomY as number) - blockH;

  const iconImg = new Image();
  iconImg.src = '/iconb-mark.png';
  const drawSize = Math.round(iconSize);
  try {
    await iconImg.decode();
    ctx.save();
    ctx.imageSmoothingEnabled = true;
    if ('imageSmoothingQuality' in ctx) {
      (ctx as CanvasRenderingContext2D & { imageSmoothingQuality: string }).imageSmoothingQuality = 'high';
    }
    ctx.drawImage(iconImg, Math.round(bx), Math.round(by + (rowH - iconSize) / 2), drawSize, drawSize);
    ctx.restore();
  } catch {
    /* icon optional */
  }

  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.font = mainFont;
  setLs(`${parseFloat(s.letterSpacing) * fontSize}px`);
  ctx.fillStyle = s.brandColor;
  ctx.fillText(label, bx + iconSize + gap, by + rowH / 2 + fontSize * 0.04);
  if (sub) {
    ctx.textBaseline = 'top';
    ctx.textAlign = 'right';
    ctx.font = subFont;
    setLs('0px');
    ctx.fillStyle = s.subColor ?? s.tldColor;
    ctx.fillText(sub, badgeRight, by + rowH + lineGap);
    ctx.textAlign = 'left';
  }
  setLs('0px');
}

/**
 * Domain line under a Heatmap export ("investmentbastion.com/heatmap"). Drawn
 * on the canvas in the logo's Inter: DOM text in html-to-image captures falls
 * back to the system face (`skipFonts`), which did not match the header mark.
 */
export const WATERMARK_FOOTER_STYLE = {
  // Small all-lowercase text: open tracking and a lighter weight keep the
  // letters from running together (the logo's tight tracking suits its size only).
  fontSize: 16,
  fontWeight: 500,
  letterSpacing: '0.015em',
  color: 'rgba(255, 255, 255, 0.78)',
  /** Logo mark green, for the tool path. */
  pathColor: '#00E179',
  canvasFontVar: '--font-inter',
  fontFamily: 'var(--font-inter), -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
  /** Space between the content's bottom edge and the line. */
  gap: 12,
} as const;

/**
 * Share-export footer, one line: "Source: investmentbastion.com · Data as of …".
 * Admin exports append the page path to the domain in the logo green
 * ("investmentbastion.com/heatmap") so Bastion's own posts point at the tool;
 * outside users get a clean attribution.
 */
export type ShareFooterLines = {
  /** Page path shown after the domain (admin only), e.g. "/heatmap"; null = bare domain. */
  path: string | null;
  /** "Oct 8, 2026" — last data refresh; null when unknown. */
  asOf: string | null;
  /** Unit note, e.g. "Values in USD"; null for none. */
  note?: string | null;
  /**
   * Draw the note + "Data as of" right-aligned on the same line, apart from the
   * source (Heatmap shares). Falls back to one left-aligned line when they would touch.
   */
  metaRight?: boolean;
  /**
   * Two floors (Heatmap boards, as the Radar Poster): the note + "Data as of"
   * on their own line, the source under it, so the logo can sit right of the source.
   */
  metaAbove?: boolean;
};

export const SHARE_FOOTER_SOURCE_FONT_SIZE = 15;
export const SHARE_FOOTER_SOURCE_COLOR = "rgba(255, 255, 255, 0.6)";

/** The footer as styled runs: plain text in the source color, the path in green. */
export function shareFooterRuns(lines: ShareFooterLines): { text: string; path: boolean }[] {
  const runs = [{ text: `Source: ${WATERMARK_BADGE_TEXT}`, path: false }];
  if (lines.path) runs.push({ text: lines.path, path: true });
  if (lines.note) runs.push({ text: ` · ${lines.note}`, path: false });
  if (lines.asOf) runs.push({ text: ` · Data as of ${lines.asOf}`, path: false });
  return runs;
}

/** Split footer: source runs on the left, "Values in USD · Data as of …" on the right. */
export function shareFooterSplitRuns(lines: ShareFooterLines): {
  left: { text: string; path: boolean }[];
  right: string;
} {
  const left = [{ text: `Source: ${WATERMARK_BADGE_TEXT}`, path: false }];
  if (lines.path) left.push({ text: lines.path, path: true });
  const right = [lines.note, lines.asOf ? `Data as of ${lines.asOf}` : null].filter(Boolean).join(" · ");
  return { left, right };
}

/** Space between the two footer floors (metaAbove). */
const SHARE_FOOTER_FLOOR_GAP = 10;

/** The metaAbove first floor: "Values in USD · Data as of …"; empty when there is none. */
function shareFooterMetaText(lines: ShareFooterLines): string {
  return [lines.note, lines.asOf ? `Data as of ${lines.asOf}` : null].filter(Boolean).join(" · ");
}

/** Bottom crop pad (CSS px) the footer needs under the content. */
export function shareFooterBand(lines: ShareFooterLines): number {
  const floor = lines.metaAbove && shareFooterMetaText(lines)
    ? SHARE_FOOTER_SOURCE_FONT_SIZE + SHARE_FOOTER_FLOOR_GAP
    : 0;
  return WATERMARK_FOOTER_STYLE.gap + floor + SHARE_FOOTER_SOURCE_FONT_SIZE;
}

/**
 * Draw the share footer left-aligned at `x`, its line starting at `top` (CSS px).
 * With `lines.metaRight` and a `rightX`, the note + date sit right-aligned at `rightX`.
 */
export async function drawShareFooter(
  ctx: CanvasRenderingContext2D,
  x: number,
  top: number,
  pixelRatio: number,
  lines: ShareFooterLines,
  rightX?: number,
): Promise<void> {
  const s = WATERMARK_FOOTER_STYLE;
  const family = resolveCanvasFontFamily(s.canvasFontVar, 'Inter');
  const size = SHARE_FOOTER_SOURCE_FONT_SIZE * pixelRatio;
  try {
    await document.fonts.load(`${s.fontWeight} ${size}px ${family}`);
  } catch {
    /* fallback stack */
  }
  ctx.save();
  ctx.font = `${s.fontWeight} ${size}px ${family}, Inter, sans-serif`;
  if ('letterSpacing' in ctx) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing =
      `${parseFloat(s.letterSpacing) * size}px`;
  }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  let cx = x * pixelRatio;
  if (lines.metaAbove) {
    const meta = shareFooterMetaText(lines);
    if (meta) {
      ctx.fillStyle = SHARE_FOOTER_SOURCE_COLOR;
      ctx.fillText(meta, cx, (top + SHARE_FOOTER_SOURCE_FONT_SIZE / 2) * pixelRatio);
      top += SHARE_FOOTER_SOURCE_FONT_SIZE + SHARE_FOOTER_FLOOR_GAP;
    }
    const sourceY = (top + SHARE_FOOTER_SOURCE_FONT_SIZE / 2) * pixelRatio;
    for (const run of shareFooterSplitRuns(lines).left) {
      ctx.fillStyle = run.path ? s.pathColor : SHARE_FOOTER_SOURCE_COLOR;
      ctx.fillText(run.text, cx, sourceY);
      cx += ctx.measureText(run.text).width;
    }
    ctx.restore();
    return;
  }
  const y = (top + SHARE_FOOTER_SOURCE_FONT_SIZE / 2) * pixelRatio;
  if (lines.metaRight && rightX != null) {
    const { left, right } = shareFooterSplitRuns(lines);
    const leftWidth = left.reduce((w, run) => w + ctx.measureText(run.text).width, 0);
    const rightWidth = right ? ctx.measureText(right).width : 0;
    const gap = 24 * pixelRatio;
    // Narrow exports: keep the classic single line rather than overlap.
    if (cx + leftWidth + gap + rightWidth <= rightX * pixelRatio) {
      for (const run of left) {
        ctx.fillStyle = run.path ? s.pathColor : SHARE_FOOTER_SOURCE_COLOR;
        ctx.fillText(run.text, cx, y);
        cx += ctx.measureText(run.text).width;
      }
      if (right) {
        ctx.fillStyle = SHARE_FOOTER_SOURCE_COLOR;
        ctx.textAlign = 'right';
        ctx.fillText(right, rightX * pixelRatio, y);
      }
      ctx.restore();
      return;
    }
  }
  for (const run of shareFooterRuns(lines)) {
    ctx.fillStyle = run.path ? s.pathColor : SHARE_FOOTER_SOURCE_COLOR;
    ctx.fillText(run.text, cx, y);
    cx += ctx.measureText(run.text).width;
  }
  ctx.restore();
}

/** Extra bottom crop pad (CSS px) that makes room for the footer domain line. */
export const WATERMARK_FOOTER_BAND = WATERMARK_FOOTER_STYLE.gap + WATERMARK_FOOTER_STYLE.fontSize;

/** Header signature without the domain row, for exports that carry the footer line. */
export function withoutSignatureDomain(style: WatermarkScreenshotStyle): WatermarkScreenshotStyle {
  return style.subLabel ? { ...style, subLabel: null } : style;
}

/** Draw `domain` + green `path` left-aligned at `x`, vertically centred on `centerY` (canvas px). */
export async function drawFooterDomain(
  ctx: CanvasRenderingContext2D,
  x: number,
  centerY: number,
  pixelRatio: number,
  path: string,
  domain: string = WATERMARK_BADGE_TEXT,
): Promise<void> {
  const s = WATERMARK_FOOTER_STYLE;
  const fontSize = s.fontSize * pixelRatio;
  const family = resolveCanvasFontFamily(s.canvasFontVar, 'Inter');
  try {
    await document.fonts.load(`${s.fontWeight} ${fontSize}px ${family}`);
  } catch {
    /* fallback stack */
  }
  ctx.save();
  ctx.font = `${s.fontWeight} ${fontSize}px ${family}, Inter, sans-serif`;
  if ('letterSpacing' in ctx) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing =
      `${parseFloat(s.letterSpacing) * fontSize}px`;
  }
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillStyle = s.color;
  ctx.fillText(domain, x, centerY);
  if (path) {
    ctx.fillStyle = s.pathColor;
    ctx.fillText(path, x + ctx.measureText(domain).width, centerY);
  }
  ctx.restore();
}

/** Outer crop pad on the canvas — watermark margins are applied inside this box. */
export type WatermarkContentInset = {
  top?: number;
  right?: number;
  bottom?: number;
  left?: number;
};

export async function drawScreenshotWatermark(
  ctx: CanvasRenderingContext2D,
  canvasWidth: number,
  pixelRatio: number,
  style: WatermarkScreenshotStyle = WATERMARK_SCREENSHOT_STYLE,
  canvasHeight?: number,
  contentInset?: WatermarkContentInset,
): Promise<void> {
  const s = style;
  const px = pixelRatio;
  const padX = s.padX * px;
  const padY = s.padY * px;
  const gap = s.gap * px;
  const dividerGap = s.dividerGap * px;
  const iconSize = s.iconSize * px;
  const fontSize = s.fontSize * px;
  const ls = parseFloat(s.letterSpacing) * fontSize;
  const showPill = s.showPill !== false && s.background !== 'transparent';
  const showDivider = s.showDivider === true;
  const contentH = Math.max(iconSize, fontSize);
  const badgeH = padY * 2 + contentH;
  const insetTop = (contentInset?.top ?? 0) * px;
  const insetRight = (contentInset?.right ?? 0) * px;
  const insetBottom = (contentInset?.bottom ?? 0) * px;
  const badgeRight = canvasWidth - insetRight - s.marginRight * px;
  const placement = s.placement ?? 'top-right';
  const marginBottom = (s.marginBottom ?? 14) * px;
  const badgeY =
    placement === 'bottom-right' && canvasHeight != null
      ? canvasHeight - insetBottom - marginBottom - badgeH
      : insetTop + s.marginTop * px;

  if (s.label) {
    const insetBottomPx = (contentInset?.bottom ?? 0) * px;
    await drawSignatureWatermark(
      ctx,
      s,
      px,
      badgeRight,
      placement === 'bottom-right' && canvasHeight != null ? null : insetTop + s.marginTop * px,
      placement === 'bottom-right' && canvasHeight != null
        ? canvasHeight - insetBottomPx - marginBottom
        : null,
    );
    return;
  }

  const monoFamily = await ensureWatermarkBadgeFont(fontSize);
  ctx.font = `${s.fontWeight} ${fontSize}px ${monoFamily}, monospace`;
  if ('letterSpacing' in ctx) {
    (ctx as CanvasRenderingContext2D & { letterSpacing: string }).letterSpacing = `${ls}px`;
  }

  const dotIdx = WATERMARK_BADGE_TEXT.lastIndexOf('.');
  let brandText = dotIdx > 0 ? WATERMARK_BADGE_TEXT.slice(0, dotIdx) : WATERMARK_BADGE_TEXT;
  let tldText = dotIdx > 0 ? WATERMARK_BADGE_TEXT.slice(dotIdx) : '';
  if (s.uppercase) {
    brandText = brandText.toUpperCase();
    tldText = s.lowercaseTld ? tldText.toLowerCase() : tldText.toUpperCase();
  }
  const brandW = ctx.measureText(brandText).width;
  const tldW = tldText ? ctx.measureText(tldText).width : 0;
  const textW = brandW + tldW;

  const dividerW = showDivider ? 1.5 * px : 0;
  const dividerBlock = showDivider ? dividerGap + dividerW + dividerGap : gap;
  const badgeW = padX + iconSize + dividerBlock + textW + padX;
  const bx = badgeRight - badgeW;
  const by = badgeY;
  const radius = s.borderRadius * px;

  if (showPill) {
    ctx.save();
    ctx.shadowColor = s.shadow;
    ctx.shadowBlur = s.shadowBlur * px;
    ctx.shadowOffsetY = 2 * px;
    ctx.beginPath();
    ctx.roundRect(bx, by, badgeW, badgeH, radius);
    ctx.fillStyle = s.background;
    ctx.fill();
    ctx.restore();

    ctx.beginPath();
    ctx.roundRect(bx, by, badgeW, badgeH, radius);
    ctx.strokeStyle = s.borderColor;
    ctx.lineWidth = 1 * px;
    ctx.stroke();
  }

  const iconImg = new Image();
  iconImg.src = '/iconb-mark.png';
  const iconX = Math.round(bx + padX);
  const iconY = Math.round(by + (badgeH - iconSize) / 2);
  const drawSize = Math.round(iconSize);
  try {
    await iconImg.decode();
    ctx.save();
    // Crisp downscale from 128px source — integer pixels, high-quality filter
    ctx.imageSmoothingEnabled = true;
    if ('imageSmoothingQuality' in ctx) {
      (ctx as CanvasRenderingContext2D & { imageSmoothingQuality: string }).imageSmoothingQuality = 'high';
    }
    ctx.drawImage(iconImg, iconX, iconY, drawSize, drawSize);
    ctx.restore();
  } catch {
    /* icon optional */
  }

  let textX = iconX + drawSize + gap;
  if (showDivider) {
    const dividerX = iconX + iconSize + dividerGap;
    ctx.beginPath();
    ctx.strokeStyle = s.divider;
    ctx.lineWidth = dividerW;
    ctx.moveTo(dividerX, by + padY);
    ctx.lineTo(dividerX, by + badgeH - padY);
    ctx.stroke();
    textX = dividerX + dividerGap;
  }

  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  // All-caps text has no descenders, so the 'middle' baseline makes it read as
  // sitting high vs the divider. Nudge uppercase text down a touch to optically
  // center it on the bar. Lowercase/mixed text keeps the true middle.
  const opticalNudge = s.uppercase ? fontSize * 0.08 : 0;
  const textY = by + badgeH / 2 + opticalNudge;
  ctx.fillStyle = s.brandColor;
  ctx.fillText(brandText, textX, textY);
  if (tldText) {
    ctx.fillStyle = s.tldColor;
    ctx.fillText(tldText, textX + brandW, textY);
  }
}

export async function ensureWatermarkBadgeFont(sizePx: number): Promise<string> {
  const family = getWatermarkMonoFamily();
  try {
    await document.fonts.load(`${WATERMARK_BADGE_STYLE.fontWeight} ${sizePx}px ${family}`);
  } catch {
    /* use fallback mono stack */
  }
  return family;
}
